// Voice Sessions — master-admin replay tool for real Alice voice sessions.
//
// Every turn of /api/voice/converse is logged (non-blocking) by
// lib/voice-turn-log into voice_turns. This page lists those sessions and
// replays any one of them turn-by-turn: what the engineer said, what Alice
// replied, and exactly what she captured. Built to make field testing
// produce actionable data instead of raw console logs.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';

// ─── Types (shape returned by /api/admin/voice-dialog/sessions[/:id]) ──────
type VoiceSession = {
  conversation_id: string;
  turn_count: number;
  retry_turns?: number;
  started_at: string;
  last_at: string;
  finished: boolean;
  last_state: string | null;
  last_section: string | null;
  cert_id: string | null;
  engineer_user_id: string | null;
  schema_id: string | null;
  engineer_name: string | null;
  cert_number: string | null;
  flags?: string[];
};
type VoiceTurn = {
  id: string;
  control: string | null;
  user_message: string | null;
  response_speech: string | null;
  response_actions: Array<Record<string, unknown>>;
  response_state: string | null;
  section_id: string | null;
  finished: boolean;
  created_at: string;
};

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtDuration(a: string, b: string): string {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (!isFinite(ms) || ms < 0) return '';
  if (ms < 60000) return Math.round(ms / 1000) + 's';
  return Math.round(ms / 60000) + 'm';
}
function StateBadge({ state }: { state: string | null }) {
  const s = state || '';
  const cls = s === 'DONE' ? 'bg-green-50 text-green-800 border-green-300'
    : s === 'PAUSED' ? 'bg-amber-50 text-amber-800 border-amber-300'
    : 'bg-gray-50 text-gray-600 border-gray-300';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${cls}`}>{s || '—'}</span>;
}
// Field-test safety-net flags surfaced on each session.
const FLAG_META: Record<string, { label: string; cls: string }> = {
  struggled: { label: '⚠ Alice struggled', cls: 'bg-amber-100 text-amber-900 border-amber-400' },
  long:      { label: '⏱ long session',    cls: 'bg-gray-100 text-gray-700 border-gray-400' },
  abandoned: { label: '⊘ abandoned',       cls: 'bg-red-100 text-red-900 border-red-400' },
};
function FlagBadge({ flag }: { flag: string }) {
  const m = FLAG_META[flag] || { label: flag, cls: 'bg-gray-100 text-gray-700 border-gray-300' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs border font-medium ${m.cls}`}>{m.label}</span>;
}
function actionLabel(a: Record<string, unknown>): { text: string; cls: string } {
  const t = String(a.type || 'action');
  if (t === 'update_field' || t === 'update_appliance_field') {
    return { text: String(a.field_id) + ' = ' + JSON.stringify(a.value), cls: 'bg-blue-50 text-blue-800 border-blue-200' };
  }
  if (t === 'set_flag') {
    return { text: 'flag ' + String(a.key) + ' = ' + JSON.stringify(a.value), cls: 'bg-violet-50 text-violet-800 border-violet-200' };
  }
  if (t === 'mark_complete') return { text: 'mark complete', cls: 'bg-green-50 text-green-800 border-green-200' };
  if (t === 'apply_engineer_signature') return { text: 'apply signature', cls: 'bg-green-50 text-green-800 border-green-200' };
  return { text: t, cls: 'bg-gray-50 text-gray-700 border-gray-200' };
}

// ─── List page ─────────────────────────────────────────────────────────────
export function VoiceSessionsListPage() {
  const [sessions, setSessions] = useState<VoiceSession[] | null>(null);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abandoned = false;
    api<{ sessions: VoiceSession[]; flagged_count?: number }>('/admin/voice-dialog/sessions')
      .then((r) => { if (!abandoned) { setSessions(r.sessions); setFlaggedCount(r.flagged_count || 0); } })
      .catch((e) => { if (!abandoned) setError(String(e.message || e)); });
    return () => { abandoned = true; };
  }, []);

  const shown = (sessions || []).filter((s) => !flaggedOnly || (s.flags && s.flags.length > 0));

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-semibold">Voice Sessions</h1>
        <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">master_admin only</span>
        <Link to="/voice-dialog" className="ml-auto px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
          🎤 Voice Dialog Editor
        </Link>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Every turn of every real Alice voice session is logged here. Open one to replay it
        turn-by-turn — what the engineer said, what Alice replied, and exactly what she captured.
        Sessions where Alice struggled, ran long, or were abandoned are flagged automatically.
      </p>

      {sessions && sessions.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          {flaggedCount > 0 ? (
            <span className="text-sm font-medium text-amber-900 bg-amber-50 border border-amber-300 rounded px-3 py-1.5">
              ⚠ {flaggedCount} session{flaggedCount === 1 ? '' : 's'} flagged for review
            </span>
          ) : (
            <span className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-1.5">
              ✓ No sessions flagged
            </span>
          )}
          {flaggedCount > 0 && (
            <button onClick={() => setFlaggedOnly((v) => !v)}
              className={`px-3 py-1.5 text-xs font-medium rounded border ${
                flaggedOnly ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}>
              {flaggedOnly ? '✓ Showing flagged only' : 'Show flagged only'}
            </button>
          )}
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm mb-4">{error}</div>}
      {!sessions && !error && <div className="text-sm text-gray-500">Loading sessions…</div>}
      {sessions && sessions.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          No voice sessions captured yet. As engineers use Alice in the field, sessions will appear here.
        </div>
      )}

      <div className="grid gap-3">
        {shown.map((s) => {
          const flagged = !!(s.flags && s.flags.length > 0);
          return (
            <Link key={s.conversation_id} to={`/voice-sessions/${encodeURIComponent(s.conversation_id)}`}
              className={`block bg-white border rounded-lg p-4 hover:shadow-sm transition ${
                flagged ? 'border-amber-300 hover:border-amber-400' : 'border-gray-200 hover:border-orange-400'
              }`}>
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <span className="font-medium text-gray-900">{s.engineer_name || 'Unknown engineer'}</span>
                {s.cert_number && <span className="font-mono text-xs text-orange-700">{s.cert_number}</span>}
                {s.finished && (
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-800 border border-green-300">✓ completed</span>
                )}
                {(s.flags || []).map((f) => <FlagBadge key={f} flag={f} />)}
                <span className="ml-auto"><StateBadge state={s.last_state} /></span>
              </div>
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>{s.turn_count} turn{s.turn_count === 1 ? '' : 's'}</span>
                {typeof s.retry_turns === 'number' && s.retry_turns > 0 && (
                  <span>{s.retry_turns} retr{s.retry_turns === 1 ? 'y' : 'ies'}</span>
                )}
                {s.last_section && <span>last section: <span className="font-mono">{s.last_section}</span></span>}
                <span>{fmtTime(s.started_at)}</span>
                <span>· {fmtDuration(s.started_at, s.last_at)}</span>
                <span className="font-mono text-gray-400">{s.conversation_id}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ─── Detail / replay page ──────────────────────────────────────────────────
export function VoiceSessionDetailPage() {
  const { conversationId } = useParams();
  const [data, setData] = useState<{ turns: VoiceTurn[]; final_snapshot: Record<string, unknown> | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSnapshot, setShowSnapshot] = useState(false);

  useEffect(() => {
    if (!conversationId) return;
    let abandoned = false;
    api<{ conversation_id: string; turns: VoiceTurn[]; final_snapshot: Record<string, unknown> | null }>(
      `/admin/voice-dialog/sessions/${encodeURIComponent(conversationId)}`
    )
      .then((r) => { if (!abandoned) setData({ turns: r.turns, final_snapshot: r.final_snapshot }); })
      .catch((e) => { if (!abandoned) setError(String(e.message || e)); });
    return () => { abandoned = true; };
  }, [conversationId]);

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/voice-sessions" className="text-sm text-gray-500 hover:text-gray-800">← All sessions</Link>
      <h1 className="text-2xl font-semibold mt-3 mb-1">Session replay</h1>
      <p className="text-xs text-gray-500 font-mono mb-6">{conversationId}</p>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm mb-4">{error}</div>}
      {!data && !error && <div className="text-sm text-gray-500">Loading transcript…</div>}
      {data && data.turns.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">No turns recorded for this session.</div>
      )}

      <div className="space-y-3">
        {data?.turns.map((t, i) => (
          <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
              <span className="font-semibold text-gray-700">Turn {i + 1}</span>
              {t.section_id && <span className="font-mono px-1.5 py-0.5 bg-gray-100 rounded">{t.section_id}</span>}
              <span className="text-gray-400">{fmtTime(t.created_at)}</span>
              <span className="ml-auto"><StateBadge state={t.response_state} /></span>
            </div>

            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">Engineer</div>
              <div className="text-sm text-gray-900">
                {t.user_message
                  ? <>&ldquo;{t.user_message}&rdquo;</>
                  : <span className="italic text-gray-400">{t.control === 'start' ? '(session start)' : '(no speech this turn)'}</span>}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wide text-orange-500 mb-0.5">Alice</div>
              <div className="text-sm text-gray-800 bg-orange-50 border border-orange-100 rounded px-3 py-2">
                {t.response_speech || <span className="italic text-gray-400">(no speech)</span>}
              </div>
            </div>

            {Array.isArray(t.response_actions) && t.response_actions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {t.response_actions.map((a, j) => {
                  const { text, cls } = actionLabel(a);
                  return <span key={j} className={`inline-block px-2 py-0.5 rounded text-xs border font-mono ${cls}`}>{text}</span>;
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {data?.final_snapshot && (
        <div className="mt-6">
          <button onClick={() => setShowSnapshot((v) => !v)}
            className="text-sm px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
            {showSnapshot ? '▾' : '▸'} Final form snapshot
          </button>
          {showSnapshot && (
            <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-96">
              {JSON.stringify(data.final_snapshot, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
